
'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Table } from '@tanstack/react-table';
import { exportToCSV, exportToExcel, exportToPDF, exportToDocx, type ExportHeader } from '../utils/exportUtils';
import type { GridConfig, ColumnConfig } from '../types';

// Removed unused ActionButton interface

interface TableControlsProps {
  table: Table<any>;
  gridConfig: GridConfig;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  setColumnVisibility: (visibility: any) => void;
  columns?: ColumnConfig[];
  isServerSideSearch?: boolean;
  /**
   * Supplies the FULL dataset for export. Required whenever the grid is
   * server-paginated (manualPagination): the table then holds only the current
   * page, so an export built from its rows would silently contain just that page.
   * Return every row the current filters select; the grid handles the rest.
   */
  exportFetchAll?: () => Promise<any[]>;
  /** Caller content at the start of the bar, before the search box. */
  toolbarStart?: React.ReactNode;
  /** Caller content at the end of the bar, before the grid's own buttons. */
  toolbarEnd?: React.ReactNode;
}

// Default export configuration
const defaultExportConfig: { enabled: boolean; formats: string[]; title?: string } = {
  enabled: true,
  formats: ['csv', 'xlsx', 'pdf', 'docx']
};

export const TableControls: React.FC<TableControlsProps> = ({
  table,
  gridConfig,
  globalFilter,
  setGlobalFilter,
  // setColumnVisibility, // Commenting out unused prop
  columns = [],
  isServerSideSearch = false,
  exportFetchAll,
  toolbarStart,
  toolbarEnd,
}) => {
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState(globalFilter || '');

  const exportMenuRef = useRef<HTMLDivElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const columnToggleRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [exportMenuPosition, setExportMenuPosition] = useState({ top: 0, left: 0 });

  // Sync local search value with external globalFilter prop
  useEffect(() => {
    setLocalSearchValue(globalFilter || '');
  }, [globalFilter]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        exportMenuRef.current && !exportMenuRef.current.contains(target) &&
        exportButtonRef.current && !exportButtonRef.current.contains(target)
      ) {
        setShowExportMenu(false);
      }
      if (columnToggleRef.current && !columnToggleRef.current.contains(target)) {
        setShowColumnToggle(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update export menu position when opened
  useEffect(() => {
    if (showExportMenu && exportButtonRef.current) {
      const rect = exportButtonRef.current.getBoundingClientRect();
      setExportMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 192, // 192px = w-48
      });
    }
  }, [showExportMenu]);

  // Merge default export config with provided config
  const exportConfig = gridConfig.export?.enabled === false
    ? { enabled: false, formats: [], title: undefined }
    : { ...defaultExportConfig, ...gridConfig.export };

  const handleExport = async (format: 'csv' | 'xlsx' | 'pdf' | 'docx') => {
    if (exporting) return;
    // With client-side pagination the table holds every row, so its filtered row
    // model IS the full set. Under server-side pagination it holds one page only,
    // and exportFetchAll goes back to the API for the rest.
    let data: any[];
    if (exportFetchAll) {
      setExporting(true);
      try {
        data = await exportFetchAll();
      } catch (err: any) {
        // No fallback to the loaded page: under server paging that is one page, and a file
        // that silently holds 25 of 4,000 rows reads as the whole register. Refuse instead.
        console.error('Export: failed to fetch all rows; no file created', err);
        const reason = err?.response?.data?.message || err?.data?.message || err?.message;
        window.alert(`Export failed${reason ? `: ${reason}` : ''}\n\nNo file was created.`);
        return;
      } finally {
        setExporting(false);
      }
    } else {
      data = table.getFilteredRowModel().rows.map(row => row.original);
    }
    const exportTitle = exportConfig.title || gridConfig.title || 'Data Export';
    const sanitizedTitle = exportTitle.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    const totalRows = data.length;
    const filename = `${sanitizedTitle}_${totalRows}_records_${new Date().toISOString().split('T')[0]}`;

    // Get visible column IDs from the table
    const visibleColumnIds = table.getAllLeafColumns()
      .filter(col => col.getIsVisible())
      .map(col => col.id);

    // Filter columns to only include visible ones (check both field and columnName)
    const visibleColumns = (columns || []).filter(col => {
      const field = col.field || '';
      const colName = col.columnName || '';
      // Match against field, columnName, or any visible column ID
      return (field && visibleColumnIds.includes(field)) ||
        (colName && visibleColumnIds.includes(colName)) ||
        visibleColumnIds.some(id => (field && id === field) || (colName && id === colName));
    });

    if (format === 'csv') {
      exportToCSV(data, visibleColumns, `${filename}.csv`, exportTitle);
    } else if (format === 'xlsx') {
      exportToExcel(data, visibleColumns, `${filename}.xlsx`, exportTitle, (exportConfig as { header?: ExportHeader }).header);
    } else if (format === 'pdf') {
      exportToPDF(data, visibleColumns, `${filename}.pdf`, exportTitle, (exportConfig as { header?: ExportHeader }).header);
    } else if (format === 'docx') {
      await exportToDocx(data, visibleColumns, `${filename}.docx`, exportTitle);
    }
    setShowExportMenu(false);
  };


  /*
   * A grid with no search panel gets a TIGHTER control bar.
   *
   * This row is laid out as "search on the left, actions on the right". With
   * `enableSearchPanel: false` the left half renders nothing, so the bar became
   * a ~58px empty strip carrying one Export button — visible above every compact
   * table on the GPBP dashboard, and on any other grid that opts out of search.
   *
   * Deliberately scoped to that case: a grid that HAS a search panel keeps the
   * padding it always had, so nothing that currently looks right can regress.
   */
  const barPadding = gridConfig.enableSearchPanel ? 'px-4 py-3 gap-4' : 'px-4 py-1.5 gap-2';

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 ${barPadding}`}>
      {toolbarStart}

      {/* Search and Controls */}
      {gridConfig.enableSearchPanel && (
        <div className="flex flex-1 items-center space-x-4">
          {/* Global Search */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="ri-search-line w-4 h-4 flex items-center justify-center text-gray-400 dark:text-gray-500"></i>
            </div>
            <input
              type="text"
              value={localSearchValue}
              onChange={(e) => {
                const newValue = e.target.value;
                setLocalSearchValue(newValue);
                if (!isServerSideSearch) {
                  setGlobalFilter(newValue);
                } else {
                  // Debounced server-side search — triggers 500ms after user stops typing
                  if (debounceRef.current) clearTimeout(debounceRef.current);
                  debounceRef.current = setTimeout(() => setGlobalFilter(newValue), 500);
                }
              }}
              onKeyDown={(e) => {
                // Allow Enter key to trigger search for server-side
                if (isServerSideSearch && e.key === 'Enter') {
                  setGlobalFilter(localSearchValue);
                }
              }}
              placeholder={isServerSideSearch ? "Search... (press Enter or click search)" : "Search across all columns..."}
              className={`block w-full pl-10 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors ${isServerSideSearch && localSearchValue ? 'pr-20' : 'pr-10'
                }`}
            />
            {/* Server-side search button */}
            {isServerSideSearch && localSearchValue && (
              <button
                onClick={() => setGlobalFilter(localSearchValue)}
                className="absolute right-10 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition-all shadow-sm hover:shadow"
                title="Click to search"
              >
                <i className="ri-search-2-line text-sm"></i>
              </button>
            )}
            {localSearchValue && (
              <button
                onClick={() => {
                  setLocalSearchValue('');
                  setGlobalFilter('');
                }}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                title="Clear search"
              >
                <i className="ri-close-line w-5 h-5"></i>
              </button>
            )}
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
            {gridConfig.totalRowCount !== undefined
              ? `${table.getFilteredRowModel().rows.length} of ${gridConfig.totalRowCount.toLocaleString()} rows`
              : `${table.getFilteredRowModel().rows.length} of ${table.getPreFilteredRowModel().rows.length} rows`
            }
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center space-x-2 ml-auto">
        {toolbarEnd}

        {/* Custom Action Button */}
        {gridConfig.actionButton && gridConfig.actionButton.visible !== false && (
          <button
            onClick={gridConfig.actionButton.onClick}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${gridConfig.actionButton.className || "bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md"}`}
          >
            {gridConfig.actionButton.icon && <i className={gridConfig.actionButton.icon}></i>}
            {gridConfig.actionButton.label}
          </button>
        )}

        {/* Grid Controls */}
        {gridConfig.enableColumnChooser && (
          <div className="relative" ref={columnToggleRef} style={{ overflow: 'visible' }}>
            <button
              onClick={() => setShowColumnToggle(!showColumnToggle)}
              className="flex items-center px-3 py-2 bg-gray-50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-600 dark:text-gray-200 transition-colors"
            >
              <i className="ri-eye-line mr-2 w-4 h-4 flex items-center justify-center"></i>
              <span className="hidden sm:inline">Columns</span>
            </button>

            {showColumnToggle && (
              <div
                className="absolute left-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl max-h-80 overflow-y-auto"
                style={{ zIndex: 9999 }}
              >
                <div className="p-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 border-b border-gray-200 dark:border-gray-600 pb-2">Toggle Columns</h4>
                  {table.getAllLeafColumns().map((column, index) => (
                    <label key={column.id} className={`flex items-center py-2 px-2 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700 ${index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'}`}>
                      <input
                        type="checkbox"
                        checked={column.getIsVisible()}
                        onChange={column.getToggleVisibilityHandler()}
                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 mr-3 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {typeof column.columnDef.header === 'string'
                          ? column.columnDef.header
                          : column.id}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Export Menu */}
        {exportConfig.enabled && (
          <div className="relative">
            <button
              ref={exportButtonRef}
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              className="flex items-center px-3 py-2 bg-gray-50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-600 dark:text-gray-200 transition-colors"
            >
              <i className={`${exporting ? 'ri-loader-4-line animate-spin' : 'ri-download-line'} mr-2 w-4 h-4 flex items-center justify-center`}></i>
              <span className="hidden sm:inline">Export</span>
            </button>

            {showExportMenu && typeof document !== 'undefined' && createPortal(
              <div
                ref={exportMenuRef}
                className="fixed w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl py-1 overflow-hidden"
                style={{ top: exportMenuPosition.top, left: exportMenuPosition.left, zIndex: 9999 }}
              >
                {exportConfig.formats.includes('csv') && (
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-green-50 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#22c55e" opacity="0.15" />
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#16a34a" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M14 2v6h6" stroke="#16a34a" strokeWidth="1.5" strokeLinejoin="round" />
                      <text x="12" y="17.5" textAnchor="middle" fill="#16a34a" fontSize="5.5" fontWeight="bold" fontFamily="Arial">CSV</text>
                    </svg>
                    <span className="font-medium">CSV</span>
                  </button>
                )}
                {exportConfig.formats.includes('xlsx') && (
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#059669" opacity="0.15" />
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#059669" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M14 2v6h6" stroke="#059669" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M8 13h8M8 16h8M8 13v6M12 13v6M16 13v6" stroke="#059669" strokeWidth="0.75" />
                    </svg>
                    <span className="font-medium">Excel</span>
                  </button>
                )}
                {exportConfig.formats.includes('pdf') && (
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#ef4444" opacity="0.15" />
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#dc2626" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M14 2v6h6" stroke="#dc2626" strokeWidth="1.5" strokeLinejoin="round" />
                      <text x="12" y="17.5" textAnchor="middle" fill="#dc2626" fontSize="5.5" fontWeight="bold" fontFamily="Arial">PDF</text>
                    </svg>
                    <span className="font-medium">PDF</span>
                  </button>
                )}
                {exportConfig.formats.includes('docx') && (
                  <button
                    onClick={() => handleExport('docx')}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#3b82f6" opacity="0.15" />
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#2563eb" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M14 2v6h6" stroke="#2563eb" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M8 13h8M8 15.5h6M8 18h5" stroke="#2563eb" strokeWidth="1" strokeLinecap="round" />
                    </svg>
                    <span className="font-medium">Word</span>
                  </button>
                )}
              </div>,
              document.body
            )}
          </div>
        )}
      </div>

    </div>
  );
};

